import { useState } from "react";
import { ArrowUpRight, Clock3, MapPin, Phone } from "lucide-react";
import { Reveal } from "./Reveal";

const inputClass =
  "w-full rounded-lg border border-cream/15 bg-onyx/60 px-4 py-3 text-sm text-cream placeholder:text-ash/60 outline-none transition-colors duration-300 focus:border-gold/60";

export const Visit = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="visit" className="relative bg-surface px-5 py-24 sm:px-8 sm:py-32" data-testid="visit-section">
      <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="eyebrow mb-5">05 · Your Table Awaits</p>
          <h2 className="font-display text-3xl leading-[1.2] tracking-tight text-cream sm:text-4xl">
            Make it a
            <br />
            <span className="gold-text italic">royal occasion.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-ash">
            A quiet breakfast, a family lunch or an unhurried dinner — call ahead and we'll keep a table warm for you.
          </p>
          <div className="mt-10 space-y-5">
            <a
              href="tel:+917358403972"
              data-testid="contact-phone-link"
              className="group flex items-center gap-4 text-cream transition-colors duration-300 hover:text-gold-light"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/35 bg-gold/10">
                <Phone size={17} className="text-gold-light" />
              </span>
              <span>
                <span className="block font-mono text-[0.6rem] uppercase tracking-[0.25em] text-ash">Call us</span>
                <span className="font-display text-xl">+91 73584 03972</span>
              </span>
            </a>
            <a
              href="https://maps.google.com/?q=The+Royal+Cafe+Anjuna+Beach+Road+North+Goa"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-location-link"
              className="group flex items-center gap-4 text-cream transition-colors duration-300 hover:text-gold-light"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/35 bg-gold/10">
                <MapPin size={17} className="text-gold-light" />
              </span>
              <span>
                <span className="block font-mono text-[0.6rem] uppercase tracking-[0.25em] text-ash">Find us</span>
                <span className="font-display text-xl">Monteiro Vaddo, Anjuna Beach Road, North Goa</span>
              </span>
              <ArrowUpRight size={16} className="text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <div className="flex items-center gap-4 text-cream" data-testid="contact-hours">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/35 bg-gold/10">
                <Clock3 size={17} className="text-gold-light" />
              </span>
              <span>
                <span className="block font-mono text-[0.6rem] uppercase tracking-[0.25em] text-ash">Hours</span>
                <span className="font-display text-xl">Open daily · 8 AM — 11 PM</span>
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="gold-ring-card rounded-2xl bg-coal p-7 sm:p-9" data-testid="reservation-card">
            {submitted ? (
              <div className="flex min-h-80 flex-col items-center justify-center text-center" data-testid="reservation-success">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/50 bg-gold/15 font-display text-2xl text-gold-light">
                  ✓
                </span>
                <h3 className="mt-6 font-display text-2xl text-cream">We'll keep a table warm.</h3>
                <p className="mt-3 max-w-xs text-sm leading-relaxed text-ash">
                  Thank you — we'll confirm your reservation by phone shortly. For anything urgent, call us on
                  73584 03972.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <p className="eyebrow mb-6">Request a table</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-[0.22em] text-ash">Name</span>
                    <input required placeholder="Your name" className={inputClass} data-testid="reservation-name-input" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-[0.22em] text-ash">Phone</span>
                    <input required type="tel" placeholder="Your phone number" className={inputClass} data-testid="reservation-phone-input" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-[0.22em] text-ash">Date</span>
                    <input required type="date" className={inputClass} data-testid="reservation-date-input" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-[0.22em] text-ash">Guests</span>
                    <select defaultValue="2" className={inputClass} data-testid="reservation-guests-select">
                      <option value="2">2 guests</option>
                      <option value="3">3 guests</option>
                      <option value="4">4 guests</option>
                      <option value="5">5+ guests</option>
                    </select>
                  </label>
                </div>
                <label className="mt-4 block">
                  <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-[0.22em] text-ash">Anything we should know?</span>
                  <textarea rows="3" placeholder="A special occasion, dietary note..." className={inputClass} data-testid="reservation-note-input" />
                </label>
                <button
                  type="submit"
                  data-testid="reservation-submit-button"
                  className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-deep px-7 py-3.5 text-sm font-semibold tracking-wide text-onyx shadow-[0_0_28px_rgba(212,175,55,0.25)] transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(212,175,55,0.45)]"
                >
                  Request a table
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
